import { EncounterEnemyConfig } from "../state/encounterEnemies";
import type { FloorDef } from "./floorConfig";
import { createSeededRng } from "../utils/rng";

export type GridCell = { row: number; col: number };
export type GridPosition = { row: number; col: number; x: number; y: number };

export const OVERWORLD_GRID = {
  columns: 3,
  rows: 4,
  marginX: 80,
  marginYTop: 100,
  marginYBottom: 80,
} as const;

export const OVERWORLD_ZONE_RADIUS = 30;

export const BOSS_GRID_CELL: GridCell = { row: 0, col: 1 };
export const REST_GRID_CELLS: GridCell[] = [
  { row: 2, col: 1 },
  { row: 2, col: 2 },
];

/**
 * Definition of an encounter zone on the overworld map.
 */
export interface EncounterZone {
  /** Unique ID for this zone */
  id: number;
  /** X position in world coordinates */
  x: number;
  /** Y position in world coordinates */
  y: number;
  /** Radius of the zone (for collision detection) */
  radius: number;
  /** Enemy configuration for this zone (optional, uses default if not provided) */
  enemyConfig?: EncounterEnemyConfig;
}

/**
 * Default encounter zones for the overworld map.
 * These are placed around the map for testing/fallback.
 */
export const DEFAULT_ENCOUNTER_ZONES: EncounterZone[] = [
  {
    id: 1,
    ...getGridCellCenter(540, 960, 1, 0),
    radius: OVERWORLD_ZONE_RADIUS,
  },
  {
    id: 2,
    ...getGridCellCenter(540, 960, 1, 2),
    radius: OVERWORLD_ZONE_RADIUS,
  },
  {
    id: 3,
    ...getGridCellCenter(540, 960, 2, 0),
    radius: OVERWORLD_ZONE_RADIUS,
  },
];

export function getGridPositions(
  worldWidth: number,
  worldHeight: number,
  grid = OVERWORLD_GRID
): GridPosition[] {
  const usableWidth = worldWidth - grid.marginX * 2;
  const usableHeight = worldHeight - (grid.marginYTop + grid.marginYBottom);
  const cellWidth = usableWidth / grid.columns;
  const cellHeight = usableHeight / grid.rows;

  const positions: GridPosition[] = [];
  for (let row = 0; row < grid.rows; row++) {
    for (let col = 0; col < grid.columns; col++) {
      positions.push({
        row,
        col,
        x: grid.marginX + cellWidth * col + cellWidth / 2,
        y: grid.marginYTop + cellHeight * row + cellHeight / 2,
      });
    }
  }
  return positions;
}

export function getGridCellCenter(
  worldWidth: number,
  worldHeight: number,
  row: number,
  col: number,
  grid = OVERWORLD_GRID
): { x: number; y: number } {
  const usableWidth = worldWidth - grid.marginX * 2;
  const usableHeight = worldHeight - (grid.marginYTop + grid.marginYBottom);
  const cellWidth = usableWidth / grid.columns;
  const cellHeight = usableHeight / grid.rows;
  return {
    x: grid.marginX + cellWidth * col + cellWidth / 2,
    y: grid.marginYTop + cellHeight * row + cellHeight / 2,
  };
}

/**
 * Generate encounter zones based on floor configuration.
 * Zones are procedurally placed based on floor theme and encounter count.
 * @param floorConfig - Floor configuration (theme, encounter count, etc.)
 * @param worldWidth - Width of the overworld world
 * @param worldHeight - Height of the overworld world
 * @param options - Optional configuration including seed, floor, encounterCount, and reservedCells
 * @returns Array of encounter zones
 */
export function generateEncounterZones(
  floorConfig: FloorDef,
  worldWidth: number,
  worldHeight: number,
  options?: {
    seed?: number;
    floor?: number;
    encounterCount?: number;
    reservedCells?: GridCell[];
  }
): EncounterZone[] {
  const baseSeed = options?.seed ?? Date.now();
  const floorSalt = options?.floor ? options.floor * 100_000 : 0;
  const rng = createSeededRng(baseSeed + floorSalt);

  // Determine encounter count from floor config
  const [minEncounters, maxEncounters] = floorConfig.encounterCountRange;
  const encounterCount = options?.encounterCount ?? rng.nextInt(minEncounters, maxEncounters + 1);

  const reservedCells = options?.reservedCells ?? [];
  const availablePositions = getGridPositions(worldWidth, worldHeight).filter(
    (position) =>
      !reservedCells.some((cell) => cell.row === position.row && cell.col === position.col)
  );

  const zones: EncounterZone[] = [];
  const maxZones = Math.min(encounterCount, availablePositions.length);

  for (let i = 0; i < maxZones; i++) {
    const index = rng.nextInt(0, availablePositions.length);
    const position = availablePositions.splice(index, 1)[0];
    zones.push({
      id: i + 1,
      x: position.x,
      y: position.y,
      radius: OVERWORLD_ZONE_RADIUS,
      // Enemy config will be set by encounter generation logic in Phase 1
    });
  }

  return zones;
}

/**
 * Legacy export for backward compatibility.
 * @deprecated Use generateEncounterZones() with floor config instead.
 */
export const ENCOUNTER_ZONES = DEFAULT_ENCOUNTER_ZONES;
